/**
 * Narrowing helpers for list-filter query params (AMP-569).
 *
 * The URL-parsed filter opts (`API_*FilterSearchParams`) type their enum fields
 * loosely as `string[]`/`string` — that hand-written schema stays loose on
 * purpose (it also does the pagination coercion and default-sort work that the
 * generated types can't). These helpers narrow those loose strings to the
 * generated enum members at the SDK boundary, using the generated enum `const`
 * as the single source of truth, so the call sites drop their `as unknown as`
 * casts without growing the hand-written layer. Values outside the enum are
 * dropped (they'd only 400 at the API).
 */

/** Narrow a loose string array to the members of a generated enum const. */
export const toEnumArray = <T extends string>(
  values: readonly string[] | undefined,
  enumConst: Record<string, T>,
): T[] | undefined => {
  if (!values) return undefined;
  const allowed = new Set<string>(Object.values(enumConst));
  return values.filter((value): value is T => allowed.has(value));
};

/** Narrow a single loose string to a member of a generated enum const. */
export const toEnumValue = <T extends string>(
  value: string | undefined,
  enumConst: Record<string, T>,
): T | undefined => {
  if (value === undefined) return undefined;
  const allowed = new Set<string>(Object.values(enumConst));
  return allowed.has(value) ? (value as T) : undefined;
};
